import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { Lock } from 'lucide-react';
import api from '../api';

function SharedView() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [password, setPassword] = useState('');
  const [needsPassword, setNeedsPassword] = useState(false);

  useEffect(() => {
    loadSharedRoadmap();
  }, [token]);

  const loadSharedRoadmap = async (pwd = null) => {
    try {
      const sharedData = await api.getSharedRoadmap(token, pwd);
      setData(sharedData);
      setNeedsPassword(false);
    } catch (err) {
      if (err.message.includes('password')) {
        setNeedsPassword(true);
      } else {
        setError(err.message || 'Failed to load shared roadmap');
      }
    } finally {
      setLoading(false);
    }
  };

  const handlePasswordSubmit = (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    loadSharedRoadmap(password);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background px-4">
        <div className="text-center">
          <h1 className="text-2xl font-bold mb-2">Error</h1>
          <p className="text-muted-foreground">{error}</p>
        </div>
      </div>
    );
  }

  if (needsPassword) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background px-4">
        <div className="max-w-md w-full">
          <div className="text-center mb-6">
            <Lock className="h-12 w-12 mx-auto mb-3 text-primary" />
            <h1 className="text-2xl font-bold mb-2">Protected Roadmap</h1>
            <p className="text-muted-foreground">This roadmap is password protected</p>
          </div>
          <form onSubmit={handlePasswordSubmit} className="space-y-4">
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Enter password"
              className="w-full px-3 py-2 border border-input rounded-md bg-background focus:outline-none focus:ring-2 focus:ring-ring"
              data-testid="password-input"
            />
            <button
              type="submit"
              className="w-full py-2 px-4 bg-primary text-primary-foreground rounded-md hover:bg-primary/90 transition-colors"
            >
              Unlock
            </button>
          </form>
        </div>
      </div>
    );
  }

  if (!data) return null;

  const { roadmap, items } = data;

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="border-b border-border bg-card">
        <div className="container mx-auto px-4 py-6">
          <h1 className="text-3xl font-bold mb-2" data-testid="shared-roadmap-title">{roadmap.name}</h1>
          {roadmap.description && (
            <p className="text-muted-foreground">{roadmap.description}</p>
          )}
          <div className="mt-4 flex items-center gap-2">
            <span className="text-sm text-muted-foreground">Read-only view</span>
          </div>
        </div>
      </header>

      {/* Content */}
      <main className="container mx-auto px-4 py-8">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {items.length === 0 ? (
            <div className="col-span-full text-center py-12 text-muted-foreground">
              No items in this roadmap
            </div>
          ) : (
            items.map((item) => (
              <div
                key={item._id}
                className="p-4 bg-card border border-border rounded-lg"
                data-testid={`shared-item-${item._id}`}
              >
                <div className="flex items-start justify-between mb-2">
                  <span className={`item-type-badge type-${item.type}`}>{item.type}</span>
                  <span className={`status-badge status-${item.status}`}>{item.status}</span>
                </div>
                <h3 className="font-semibold mb-2">{item.title}</h3>
                {item.description && (
                  <p className="text-sm text-muted-foreground mb-3">{item.description}</p>
                )}
                <div className="flex items-center justify-between text-xs text-muted-foreground">
                  <span className="capitalize">{item.priority} priority</span>
                  <span className="capitalize">{item.timeframe}</span>
                </div>
              </div>
            ))
          )}
        </div>
      </main>

      {/* Footer */}
      <footer className="border-t border-border mt-12">
        <div className="container mx-auto px-4 py-6 text-center text-sm text-muted-foreground">
          Powered by <span className="font-semibold upmap-logo" style={{ fontSize: '1rem', color: '#82d230' }}>u</span><span className="font-semibold" style={{ color: '#82d230' }}>pmap</span>
        </div>
      </footer>
    </div>
  );
}

export default SharedView;
